/**
 * CompliancePage — the review queue, and the superadmin's controls.
 *
 * REVIEW, NOT APPROVAL. Nothing on this page blocks anything. Every row
 * describes something that ALREADY happened. The reviewer's job is to look at
 * it afterwards and either sign it off or flag it. There is deliberately no
 * "approve" or "reject" button, because there is nothing waiting on them.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Stack, Switch, Alert, CircularProgress, IconButton,
  Select, MenuItem, FormControl, InputLabel, Tooltip, useMediaQuery, Divider, Card,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Gavel, CheckCircle, Flag, Delete, PersonAdd, Refresh, Info,
} from '@mui/icons-material';
import { complianceApi, usersApi } from '../services/api';
import { useWSRefresh } from '../contexts/WebSocketContext';

const STATUS_CHIP: Record<string, any> = {
  pending:  { bgcolor: '#fef3c7', color: '#92400e' },
  reviewed: { bgcolor: '#dcfce7', color: '#166534' },
  flagged:  { bgcolor: '#fee2e2', color: '#b91c1c' },
};

function when(v?: string) {
  if (!v) return '—';
  const d = new Date(v + (v.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function CompliancePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState<any>({});
  const [reviews, setReviews] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [reviewers, setReviewers] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [decide, setDecide] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newReviewer, setNewReviewer] = useState<{ user_id: number | ''; action_key: string }>({ user_id: '', action_key: '' });

  const canConfigure = !!summary.is_superadmin;

  const load = useCallback(() => {
    Promise.all([
      complianceApi.summary().catch(() => ({ data: {} })),
      complianceApi.reviews({ status: statusFilter }).catch(() => ({ data: { reviews: [] } })),
      complianceApi.actions().catch(() => ({ data: { actions: [] } })),
      complianceApi.reviewers().catch(() => ({ data: { reviewers: [] } })),
    ]).then(([s, r, a, rv]) => {
      setSummary(s.data || {});
      setReviews(r.data.reviews || []);
      setActions(a.data.actions || []);
      setReviewers(rv.data.reviewers || []);
      setErr('');
    }).catch(() => setErr('Could not load the compliance data.'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { usersApi.list().then(r => setUsers(r.data || [])).catch(() => {}); }, []);
  useWSRefresh(['ticket:updated', 'ticket:status'], load);

  const submitDecision = async (status: 'reviewed' | 'flagged') => {
    if (!decide) return;
    setBusy(true);
    try {
      await complianceApi.review(decide.id, { review_status: status, reviewer_notes: notes || null });
      setDecide(null); setNotes(''); load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not save the review.');
    } finally { setBusy(false); }
  };

  const toggleAction = async (key: string, enabled: boolean) => {
    setActions(a => a.map(x => x.action_key === key ? { ...x, enabled: enabled ? 1 : 0 } : x));
    try { await complianceApi.toggleAction(key, enabled); } catch { load(); }
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  if (!summary.is_admin && !summary.is_reviewer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">You are not a compliance reviewer. A superadmin assigns reviewers.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        <Gavel sx={{ color: '#9333ea' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Compliance Review</Typography>
        <Chip size="small" label={`${summary.pending || 0} pending`} sx={{ fontWeight: 700, ...STATUS_CHIP.pending }} />
        {!!summary.flagged && <Chip size="small" label={`${summary.flagged} flagged`} sx={{ fontWeight: 700, ...STATUS_CHIP.flagged }} />}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={load}><Refresh /></IconButton>
      </Stack>

      <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
        <b>Review, not approval.</b> Nothing here is waiting on you — every action below already happened.
        Reviewing an item records your conclusion about it; it does not undo or unblock anything.
      </Alert>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? 'scrollable' : 'standard'}
            allowScrollButtonsMobile sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Tab label="Review queue" />
        <Tab label="Reviewable actions" />
        <Tab label="Reviewers" />
      </Tabs>

      {/* ── QUEUE ─────────────────────────────────────────────────── */}
      {tab === 0 && (
        <>
          <FormControl size="small" sx={{ mb: 1.5, minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="reviewed">Reviewed</MenuItem>
              <MenuItem value="flagged">Flagged</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>

          {isMobile ? (
            <Stack spacing={1}>
              {reviews.map(r => (
                <Card key={r.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.action_label || r.action_key}</Typography>
                    <Chip size="small" label={r.review_status} sx={{ fontWeight: 700, height: 20, ...STATUS_CHIP[r.review_status] }} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {r.ticket_number || '—'} · {r.performed_by_name || 'system'} · {when(r.performed_at)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                    Reviewer: {r.assigned_reviewer_name || 'unassigned'}
                  </Typography>
                  {r.review_status === 'pending' && (
                    <Button size="small" sx={{ mt: 0.5 }} onClick={() => { setDecide(r); setNotes(''); }}>Review</Button>
                  )}
                </Card>
              ))}
              {reviews.length === 0 && <Typography variant="body2" color="text.secondary">Nothing to review.</Typography>}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ticket</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Performed by</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Assigned reviewer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Reviewed</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviews.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.action_label || r.action_key}</Typography>
                        {r.details_json && (
                          <Tooltip title={<pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(r.details_json, null, 1)}</pre>}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', cursor: 'help' }}>details</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.ticket_number || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.performed_by_name || 'system'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{when(r.performed_at)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.assigned_reviewer_name || <em style={{ color: '#94a3b8' }}>unassigned</em>}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.review_status} sx={{ fontWeight: 700, height: 20, ...STATUS_CHIP[r.review_status] }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {r.reviewed_at ? `${r.reviewed_by_name || ''} · ${when(r.reviewed_at)}` : '—'}
                        {r.reviewer_notes && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>{r.reviewer_notes}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.review_status === 'pending' && (
                          <Button size="small" onClick={() => { setDecide(r); setNotes(''); }}>Review</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {reviews.length === 0 && (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      Nothing to review.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── ACTIONS (superadmin toggles) ───────────────────────────── */}
      {tab === 1 && (
        <>
          {!canConfigure && <Alert severity="info" sx={{ mb: 2 }}>Only a superadmin can change which actions are reviewed.</Alert>}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Reviewable action</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Severity</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Reviewers</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Pending</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Reviewed?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actions.map(a => (
                  <TableRow key={a.action_key} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={a.severity} variant="outlined"
                            sx={{ height: 19, fontSize: 10,
                                  borderColor: a.severity === 'high' ? '#fca5a5' : '#e2e8f0',
                                  color: a.severity === 'high' ? '#b91c1c' : '#64748b' }} />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12 }}>
                      {a.reviewer_count ? a.reviewer_count : <span style={{ color: '#dc2626' }}>none</span>}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12 }}>{a.pending_count || 0}</TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={!!a.enabled} disabled={!canConfigure}
                              onChange={e => toggleAction(a.action_key, e.target.checked)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Turning an action on starts recording it for review from that moment. It never blocks the action.
          </Typography>
        </>
      )}

      {/* ── REVIEWERS (superadmin assigns) ─────────────────────────── */}
      {tab === 2 && (
        <>
          <Stack direction="row" sx={{ mb: 1.5 }} spacing={1}>
            <Button size="small" variant="contained" startIcon={<PersonAdd />} disabled={!canConfigure}
                    onClick={() => setAddOpen(true)}>Assign a reviewer</Button>
          </Stack>
          {!canConfigure && <Alert severity="info" sx={{ mb: 2 }}>Only a superadmin can assign reviewers.</Alert>}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Reviewer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reviews</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Assigned</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {reviewers.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.user_name || r.user_email}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.user_email}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.action_label}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{when(r.assigned_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" disabled={!canConfigure}
                                  onClick={async () => { await complianceApi.revokeReviewer(r.id); load(); }}>
                        <Delete sx={{ fontSize: 17 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {reviewers.length === 0 && (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No reviewers assigned yet. Reviewable actions will still be recorded — they will simply sit unassigned.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* review dialog */}
      <Dialog open={!!decide} onClose={() => !busy && setDecide(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Review: {decide?.action_label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>{decide?.performed_by_name || 'system'}</b> · {when(decide?.performed_at)}
            {decide?.ticket_number ? ` · ${decide.ticket_number}` : ''}
          </Typography>
          {decide?.details_json && (
            <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, p: 1, mb: 2 }}>
              <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(decide.details_json, null, 1)}
              </pre>
            </Box>
          )}
          <Divider sx={{ mb: 2 }} />
          <TextField fullWidth multiline rows={3} label="Reviewer notes" value={notes}
                     onChange={e => setNotes(e.target.value)} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This is recorded permanently and cannot be rewritten later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDecide(null)} disabled={busy}>Cancel</Button>
          <Button color="error" startIcon={<Flag />} disabled={busy}
                  onClick={() => submitDecision('flagged')}>Flag</Button>
          <Button variant="contained" color="success" startIcon={<CheckCircle />} disabled={busy}
                  onClick={() => submitDecision('reviewed')}>Mark reviewed</Button>
        </DialogActions>
      </Dialog>

      {/* assign reviewer dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Assign a reviewer</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>User</InputLabel>
            <Select value={newReviewer.user_id} label="User"
                    onChange={e => setNewReviewer({ ...newReviewer, user_id: Number(e.target.value) })}>
              {users.map((u: any) => (
                <MenuItem key={u.id} value={u.id}>
                  {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Reviews</InputLabel>
            <Select value={newReviewer.action_key} label="Reviews"
                    onChange={e => setNewReviewer({ ...newReviewer, action_key: e.target.value })}>
              <MenuItem value="">All enabled actions</MenuItem>
              {actions.map(a => <MenuItem key={a.action_key} value={a.action_key}>{a.label}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newReviewer.user_id}
                  onClick={async () => {
                    await complianceApi.addReviewer({
                      user_id: Number(newReviewer.user_id),
                      action_key: newReviewer.action_key || null,
                    });
                    setAddOpen(false); setNewReviewer({ user_id: '', action_key: '' }); load();
                  }}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
